import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import type { RolePermissions } from "../../components/PermissionsContext";
import { PermissionsEditor } from "../PermissionsEditor";
import "./RoleDrawer.css";

export type DrawerMode = "view" | "edit" | "create";

export type RoleDrawerProps = {
  open: boolean;
  mode: DrawerMode;
  name: string;
  permissions: RolePermissions;
  memberCount?: number;
  onClose: () => void;
  onNameChange: (name: string) => void;
  onPermissionsChange: (perms: RolePermissions) => void;
  onSave: () => void;
  onSwitchToEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  dirty: boolean;
};

export const RoleDrawer: React.FC<RoleDrawerProps> = (props) => {
  function handleBackdrop() {
    if (props.dirty && !window.confirm("Descartar mudanças?")) return;
    props.onClose();
  }

  React.useEffect(() => {
    if (!props.open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleBackdrop();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.dirty]);

  if (!props.open) return null;

  const isView = props.mode === "view";
  const title = props.mode === "create" ? "Novo cargo" : props.name || "Cargo";

  return (
    <>
      <div className="roleDrawerBackdrop" onClick={handleBackdrop} />
      <aside className="roleDrawer" role="dialog" aria-label={title}>
        <header className="roleDrawerHeader">
          {isView ? (
            <h3 className="roleDrawerTitle">{title}</h3>
          ) : (
            <input
              className="roleDrawerNameInput"
              value={props.name}
              onChange={(e) => props.onNameChange(e.target.value)}
              placeholder="Nome do cargo"
              autoFocus
            />
          )}
          <button
            className="roleDrawerCloseBtn"
            onClick={handleBackdrop}
            aria-label="Fechar"
            type="button"
          >
            <CloseIcon />
          </button>
        </header>

        {isView && (
          <div className="roleDrawerActions">
            <button className="roleDrawerActionBtn" onClick={props.onSwitchToEdit} type="button">
              <EditIcon fontSize="small" /> Editar
            </button>
            <button className="roleDrawerActionBtn" onClick={props.onClone} type="button">
              <ContentCopyIcon fontSize="small" /> Clonar
            </button>
            <button
              className="roleDrawerActionBtn roleDrawerActionBtnDanger"
              onClick={props.onDelete}
              type="button"
            >
              <DeleteIcon fontSize="small" /> Excluir
            </button>
          </div>
        )}

        {isView && typeof props.memberCount === "number" && (
          <div className="roleDrawerMeta">{props.memberCount} colaboradores</div>
        )}

        <div className="roleDrawerBody">
          <PermissionsEditor
            value={props.permissions}
            onChange={props.onPermissionsChange}
            readOnly={isView}
          />
        </div>

        {!isView && (
          <footer className="roleDrawerFooter">
            <button className="roleDrawerCancelBtn" onClick={handleBackdrop} type="button">
              Cancelar
            </button>
            <button
              className="roleDrawerSaveBtn"
              onClick={props.onSave}
              disabled={!props.name.trim()}
              type="button"
            >
              Salvar
            </button>
          </footer>
        )}
      </aside>
    </>
  );
};
